import { useRef } from 'react';

import { useVoiceStore } from '../state/voice-ui.store';

const DRAG_THRESHOLD_PX = 4;
const EDGE_MARGIN = 16;
const FALLBACK_WIDTH = 320;

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), Math.max(min, max));
}

interface IDragState {
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    width: number;
    dragging: boolean;
}

export interface IWidgetDragHandlers {
    readonly onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
    readonly onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => void;
    readonly onPointerUp: (event: React.PointerEvent<HTMLDivElement>) => void;
    readonly onClickCapture: (event: React.MouseEvent<HTMLDivElement>) => void;
}

/**
 * Drag behavior for the expanded widget panel (via its header — the
 * minimized launcher is a fixed badge in the app header and never drags).
 * A ~4px movement threshold separates clicks from drags — below it the press
 * stays a click; past it the widget follows the pointer and the trailing
 * click is swallowed. Clamp width is measured live so the 320px panel stops
 * at the viewport edge.
 */
export function useWidgetDrag(options: { skipInteractive?: boolean } = {}): IWidgetDragHandlers {
    const { skipInteractive = false } = options;
    const position = useVoiceStore((s) => s.position);
    const setPosition = useVoiceStore((s) => s.setPosition);
    const dragRef = useRef<IDragState | null>(null);
    const wasDragRef = useRef(false);

    const onPointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
        // Header mode: presses on interactive children (minimize button) stay
        // clicks — capturing them would retarget the pointer stream.
        if (skipInteractive && (event.target as HTMLElement).closest('button')) return;
        const rect = event.currentTarget
            .closest('[data-slot="voice-widget"]')
            ?.getBoundingClientRect();
        const width = rect?.width ?? FALLBACK_WIDTH;
        wasDragRef.current = false;
        // Deliberately NOT capturing the pointer yet — capture retargets the
        // derived click to this wrapper, killing taps on inner buttons (the
        // pill). Capture happens at the drag threshold in onPointerMove.
        dragRef.current = {
            startX: event.clientX,
            startY: event.clientY,
            originX: position?.x ?? rect?.left ?? window.innerWidth - width - EDGE_MARGIN,
            originY: position?.y ?? rect?.top ?? window.innerHeight - 400,
            width,
            dragging: false,
        };
    };

    const onPointerMove = (event: React.PointerEvent<HTMLDivElement>): void => {
        const drag = dragRef.current;
        if (!drag) return;
        // Stale press (pointerup happened off-element without capture).
        if (event.buttons === 0) {
            dragRef.current = null;
            return;
        }
        const dx = event.clientX - drag.startX;
        const dy = event.clientY - drag.startY;
        if (!drag.dragging && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
        if (!drag.dragging) {
            drag.dragging = true;
            wasDragRef.current = true;
            event.currentTarget.setPointerCapture(event.pointerId);
        }
        setPosition({
            x: clamp(drag.originX + dx, EDGE_MARGIN, window.innerWidth - drag.width - EDGE_MARGIN),
            y: clamp(drag.originY + dy, EDGE_MARGIN, window.innerHeight - 80),
        });
    };

    const onPointerUp = (event: React.PointerEvent<HTMLDivElement>): void => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        dragRef.current = null;
    };

    const onClickCapture = (event: React.MouseEvent<HTMLDivElement>): void => {
        // Swallow the click that trails a completed drag gesture.
        if (wasDragRef.current) {
            event.preventDefault();
            event.stopPropagation();
            wasDragRef.current = false;
        }
    };

    return { onPointerDown, onPointerMove, onPointerUp, onClickCapture };
}
